<template>
  <div
    v-if="garageData"
    class="garage-services-page"
  >
    <Transition name="bar">
      <div
        class="garage-services-page__header-wrapper"
        v-show="!searchInputFocus"
      >
        <div>
          <NuxtLink
            class="garage-services-page__header"
            :to="`/mechanicy/${garageData?.garage.citySlug}/${garageData?.garage.nameSlug}.html`"
          >
            <NuxtImg
              class="garage-services-page__header-icon"
              src="/_nuxt/chevronBack.svg"
              height="22px"
              alt="Szczegóły wizyty"
              width="22px"
            />
            <h5>Czego potrzebujesz?</h5>
          </NuxtLink>
        </div>
      </div>
    </Transition>

    <div class="garage-services-page__container">
      <div class="garage-services-page__wrapper">
        <Transition name="bar">
          <div v-show="!searchInputFocus">
            <h3>Wybierz rodzaj wizyty</h3>
          </div>
        </Transition>
        <template v-if="form.fields.serviceType.key === EGarageServiceType.Service">
          <div class="garage-services-page__search-bar">
            <div class="garage-services-page__search-bar-wrapper">
              <DmRadioButton
                v-if="!!garageData.garageOtherServiceAvailability?.available"
                id="serviceTypeRadioButton"
                v-model="form.fields.serviceType"
                :items="serviceTypes"
                class="garage-services-page__tabs"
              />
              <div class="garage-services-page__search-input-wrapper">
                <DmInput
                  v-model="form.fields.search"
                  class="garage-services-page__search-input"
                  icon="i-search"
                  id="searchServiceInput"
                  label="Szukaj usługi"
                  placeholder="np. Wymiana sprzęgła"
                  @blur="searchInputFocus = false"
                  @focus="searchInputFocus = true"
                  @update:model-value="searchUpdate"
                />
                <Transition>
                  <div
                    v-show="searchInputFocus"
                    class="garage-services-page__search-bar-button"
                    role="button"
                    @click="searchInputFocus = false"
                  >Anuluj</div>
                </Transition>
              </div>
            </div>
          </div>
          <GaragePriceList
            v-if="garageData.garagePriceList"
            :garage-price-list="filteredGaragePriceList"
            @set-other-reason-tab="setOtherReasonTab()"
          />
        </template>

        <template v-else>
          <DmRadioButton
            v-if="!!garageData.garageOtherServiceAvailability?.available"
            id="serviceTypeRadioButton"
            v-model="form.fields.serviceType"
            :items="serviceTypes"
            class="garage-services-page__tabs"
          />
          <form
            id="repair-reason.form"
            class="garage-services-page__form"
            @submit.prevent
          >

            <h4 class="repair-reason-header">Co wymaga naprawy?</h4>
            <div class="repair-reason-radios">
              <div
                v-for="(item, index) in computedRepairCategories"
                :key="`${item.id}`"
                @click="selectRepairCategory(item.id)"
                @keyup.enter="selectRepairCategory(item.id)"
                class="repair-reason-radio"
              >
                <input
                  :id="`${item.id}`"
                  class="repair-reason-radio__input"
                  type="radio"
                  name="repair-reason-service"
                  tabindex="-1"
                  :checked="form.fields.serviceId === item.id"
                >
                <div
                  :tabindex="form.fields.serviceId === item.id ? -1 : 0"
                  class="repair-reason-radio__checkmark"
                  :class="{ 'repair-reason-radio__checkmark--checked': form.fields.serviceId === item.id }"
                />
                <label
                  class="repair-reason-radio__text"
                  :for="`${item.id}`"
                >
                  <NuxtImg
                    :src="`/_nuxt/repair-categories/${item.id}.svg`"
                    width="30px"
                    height="30px"
                  />
                  <div>
                    {{ item.categoryName }}
                    <div
                      class="repair-reason-radio__description"
                      v-if="item.id === otherRepairsId"
                    >Kliknij tutaj, opisz problem i umów wizytę.</div>
                  </div>
                </label>
              </div>
            </div>
          </form>
        </template>
      </div>
    </div>
  </div>
  <DmLoader
    v-if="!!pending"
    icon-size="icon-lg"
  />

  <ClientOnly>
    <DmModal
      :modelValue="modal"
      @update:modelValue="onCloseRepairModal"
      title="Dodatkowy opis (opcjonalnie)"
    >
      <div>
        <div class="garage-services-page__badge mb-4">
          <i class="i-info" />
          <p class="garage-services-page__badge-paragraph">Dodatkowe informacje przyspieszą diagnostykę usterki i
            naprawę.</p>
        </div>

        <DmTextarea
          id="repair-reason.textarea"
          class="repair-reason-textarea"
          label="Opisz krótko problem z samochodem"
          placeholder="np. Podczas jazdy stuka coś w zawieszeniu"
          v-model="form.fields.repairReason"
          :required="true"
          pattern="\S{3,}"
          @update:errors="updateErrors($event, 'repair-reason')"
        />

        <div class="garage-services-page__helpers">
          <div
            v-for="reason in repairReasons"
            class="garage-services-page__helper"
            :class="{ 'garage-services-page__helper--selected': isHelperSelected(reason) }"
            @click="addReason(reason)"
          >
            {{ reason }}
          </div>
        </div>
      </div>
      <DmButton
        @click.prevent="submit"
        title="Wybierz termin"
        class="w-100 mb-4 mt-5"
        @click="modal = true"
      />
    </DmModal>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useFormCreator } from '@/composables/useFormCreator';
import DmRadioButton from '@/components/UIComponents/DmRadioButton.vue';
import { DmLoader, DmInput, DmTextarea, DmButton, DmDescriptionButton, DmModal } from '@dobrymechanik/vue-ui-v2';
import type { IDmComponentsItem } from '~/models/components';
import { appRequests } from '~/api/app-instance';
import type { GarageAutobookingServiceStatus, LegacyService, VirtualServiceCategory } from '~/api/docs/app';
import { useStore } from '~/store/store';
import { apiRequests } from '~/api/api-instance';
import type { GarageJsonldGarageReadGarageReadIdGarageReadNameGarageSlugHistoryReadSuggestedGarageRead } from '~/api/docs/api';

definePageMeta({
  layout: "modal",
});

enum EGarageServiceType {
  Service,
  Issue
}

interface IGarageServicesFields {
  serviceType: IDmComponentsItem;
  search: string;
  repairReason: string;
  serviceId: string;
}

useHead({
  meta: [
    { hid: 'robots', name: 'robots', content: 'noindex, nofollow' }
  ]
});

const searchInputFocus: Ref<boolean> = ref(false);
const modal: Ref<boolean> = ref(false);
const reasonsArr: Ref<string[]> = ref([]);

// computed
const serviceTypes = computed(() => {
  return [
    {
      key: EGarageServiceType.Service, text: 'Usługa z cennika', metadata: { icon: 'i-history' }
    },
    {
      key: EGarageServiceType.Issue, text: 'Naprawa usterki', metadata: { icon: 'i-edit' }
    }
  ];
});

const filteredGaragePriceList = computed(() => {
  const list = garageData.value?.garagePriceList || [];

  return list.filter(service => {
    return service.name?.toLowerCase().includes(form.value.fields.search.toLocaleLowerCase());
  });
});

const isAnyService = computed(() => {
  return filteredGaragePriceList.value?.length;
});

const garagePath = computed(() => {
  return route.params['garageId'] as string;
});

const repairReasons = computed(() => {
  return garageData.value?.garageVitualPriceList.find(el => el.id === form.value.fields.serviceId)?.pills;
});

const computedRepairCategories = computed(() => {
  const otherRepair = {
    categoryName: 'Nie wiesz co wybrać?',
    id: otherRepairsId,
    pills: [],
    priceName: '',
    requiredSeoServiceSlug: '',
    requiredSeoServiceSlugId: 0
  };

  return [...garageData.value?.garageVitualPriceList || [], otherRepair];
});

// use
const router = useRouter();
const route = useRoute();
const { form, valid, updateErrors, checkFormValidity } = useFormCreator<IGarageServicesFields>({
  serviceType: serviceTypes.value[0],
  search: '',
  repairReason: '',
  serviceId: ''
});
const store = useStore();
const { $posthog } = useNuxtApp();
const nuxtApp = useNuxtApp();


const otherRepairsId: string = '9f1331f2-66c0-4341-ad06-cc6f074e1857';


interface IGarageDataForServices {
  garage: GarageJsonldGarageReadGarageReadIdGarageReadNameGarageSlugHistoryReadSuggestedGarageRead;
  garagePriceList: LegacyService[];
  garageOtherServiceAvailability: GarageAutobookingServiceStatus;
  garageVitualPriceList: VirtualServiceCategory[];
}

// watch 
watch(() => route.query, (queries) => {
  if (form.value.fields.serviceType.key === EGarageServiceType.Service && queries?.["other-reason"]) {
    setOtherReasonTab();
  }
});

watch(() => form.value.fields.serviceType, (serviceType) => {
  if (serviceType.key === EGarageServiceType.Issue) {
    router.replace({ query: { "other-reason": "true" } });
  } else {
    router.replace({ query: {} });
  }

});

// methods
const { data: garageData, pending, error, refresh } = await useAsyncData(
  "garageDataForServices",
  async () => {
    const cachedGarageData = useNuxtApp().payload.data.garageData;
    if (!!cachedGarageData) return cachedGarageData as IGarageDataForServices;

    const { data: { id } } = await appRequests.getAppGarageIdByUrl(garagePath.value);
    const idString = id?.toString();

    if (!idString) return;
    const GETgarage = apiRequests.apiGarageIdGet(idString);
    const GETgaragePriceList = appRequests.getAppGaragePriceList(idString);
    const GETotherServiceAvailability = appRequests.getAppGarageAutobookingServiceAvailability(idString, otherRepairsId);
    const GEThelpers = appRequests.getAppGarageVirtualServiceCategoriesList(idString);

    const [garage, garagePriceList, garageOtherServiceAvailability, garageVitualPriceList] =
      await Promise.all([
        (await GETgarage).data,
        (await GETgaragePriceList).data,
        (await GETotherServiceAvailability).data,
        (await GEThelpers).data
      ]);

    return {
      garage, garagePriceList, garageOtherServiceAvailability, garageVitualPriceList
    };
  }, {
  lazy: true
});

if (!garageData.value && !nuxtApp.payload.data && !nuxtApp.payload.data) {
  throw createError({ statusCode: 404, message: 'Strona nie została odnaleziona', fatal: true });
}

const addReason = (reason: string) => {
  const include = reasonsArr.value.includes(reason);

  if (include) {
    reasonsArr.value = reasonsArr.value.filter(el => el !== reason);
  } else {
    reasonsArr.value.push(reason);
  }

  let reasonStr: string = '';

  reasonsArr.value.forEach((reason, index) => {
    if (index === 0) {
      reasonStr = reasonStr + reason;
    } else {
      reasonStr = reasonStr + '; ' + reason;
    }
  });

  form.value.fields.repairReason = reasonStr.trim();

  const textArea = document.getElementById('repair-reason.textarea');
  textArea?.focus();
  setTimeout(() => textArea?.blur(), 0);
};

const submit = async () => {
  const isFormValid = await checkFormValidity('repair-reason.form');

  if (isFormValid) {
    let subname: string = garageData.value?.garageVitualPriceList.find(category => {
      return category.id === form.value.fields.serviceId;
    })?.categoryName || 'Naprawa';

    const dsc = subname + (form.value.fields.repairReason ? `: ${form.value.fields.repairReason}` : '');
    router.push({ name: `mechanicy-city-garageId-services-serviceId`, params: { serviceId: otherRepairsId }, query: { repairDescription: dsc } });
  }
};

const setOtherReasonTab = () => {
  form.value.fields.serviceType = serviceTypes.value[1];
};

const isHelperSelected = (helper: string) => {
  const regExp = new RegExp(helper, 'gmi');
  return !!regExp.test(form.value.fields.repairReason);
};

const searchUpdate = (val: string) => {
  if (!isAnyService.value) {
    $posthog()?.capture('no-results', {
      value: val,
      page: 'workshop-services',
      type: 'services'
    });
  }
};

const selectRepairCategory = (item?: string | null) => {
  if (!item) return;
  form.value.fields.serviceId = item;
  modal.value = true;
};

const onCloseRepairModal = () => {
  form.value.fields.repairReason = '';
  reasonsArr.value = [];
  modal.value = false;
};

onMounted(() => {
  setTimeout(() => {
    if (route.query['other-reason']) {
      setOtherReasonTab();
    }
    $posthog()?.capture('$pageview', {
      garageType: !!garageData.value?.garage.premium ? 'premium' : 'free',
      garageId: garageData.value?.garage.id || null
    });
  }, 0);
})


</script>

<style lang="scss">
@import url('assets/styles/pages/garage-services.scss');

.repair-reason-header {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
}

.repair-reason-radios {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0;
}

.repair-reason-radio {
  flex: 1;
  min-width: 160px;
  position: relative;
  min-height: 58px;
  display: flex;
  align-items: center;
  padding: 4px 12px;
  cursor: pointer;

  @media (min-width: $breakpoint) {
    min-width: 300px;
  }

  &__checkmark {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border: 1px solid $grey-4;
    border-radius: 10px;
  }

  &__input {
    margin: 0;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__text {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__description {
    color: $grey-3;
    font-size: 14px;
    line-height: 18px;
    margin-top: 2px;
  }
}

.repair-reason-description-button {
  width: 100%;

  .description-button__description {
    margin-right: 0;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

.repair-reason-textarea {
  textarea {
    min-height: 100px !important;
  }
}

.repair-reason-carousel {
  .carousel__track {
    gap: 0 10px;
  }
}
</style>